/**
 * 고정 타임스텝 루프.
 *
 * 시뮬레이션은 30Hz로만 돈다. 화면이 144Hz든 30Hz든 같은 횟수만큼 돈다.
 * 렌더링은 남은 시간만큼 보간해서 부드럽게 보이게 한다.
 * 이 분리가 없으면 프레임률에 따라 결과가 달라져서 리플레이 검증이 불가능해진다.
 */
import { CmdType, CommandLog, cmd } from '../sim/commands.ts';
import type { Command } from '../sim/commands.ts';
import { TICK_HZ } from '../sim/units.ts';
import type { World } from '../sim/world.ts';
import type { Renderer } from '../render/renderer.ts';
import type { InputState } from './input.ts';

const TICK_MS = 1000 / TICK_HZ;
/** 한 프레임에 따라잡을 수 있는 최대 틱. 탭을 오래 숨겼다 돌아왔을 때 폭주를 막는다. */
const MAX_CATCHUP = 5;

export interface LoopHooks {
  onTick?: (world: World) => void;
  onFrame?: (fps: number, simMs: number) => void;
}

/**
 * 선택 마스크에서 없는 부대를 걷어낸다. 남는 것이 없으면 0(싣지 않는다).
 *
 * 숫자키 2는 부하 부대가 없어도 눌린다. 그대로 실으면 시뮬레이션은 닫힌 부대를
 * 골라 둔 채 명령을 전부 건너뛰고, 화면은 '내 부대'라고 보여 준다 — 후퇴를 눌렀는데
 * 아무도 물러나지 않는다. 리뷰가 잡았다. 시뮬레이션이 아니라 여기서 거르는 이유:
 * 규칙(리플레이 버전)을 건드리지 않고, 애초에 없는 선택을 로그에 남기지 않기 위해서다.
 */
export function normalizeSelect(mask: number, sqUsed: ArrayLike<number>): number {
  let used = 0;
  for (let sq = 0; sq < sqUsed.length; sq++) if (sqUsed[sq]) used |= 1 << sq;
  return mask & used;
}

/** 판을 세운 까닭(`GameLoop.hold`). 서로 따로 풀린다. */
export const HOLD_PICK = 1;
export const HOLD_LEAVE = 2;

export class GameLoop {
  readonly log = new CommandLog();
  private accumulator = 0;
  private last = 0;
  private lastDx = 0;
  private lastDy = 0;
  private simMs = 0;
  private frames = 0;
  private fpsClock = 0;
  private fps = 0;
  private running = false;
  private paused = false;
  /**
   * 부대를 고르는 동안 세운다(`hold`). 전투가 끝나 세운 것(`paused`)과 따로 둔다 — 풀 때 끝난 전투가 다시 돌면
   * 안 된다. 세운 동안에도 그리기와 입력은 돈다: 카메라가 지휘관에게 가고, 경로를 그린다. 그 사이 쌓인 선택·
   * 경로는 풀린 뒤 첫 틱에 실린다 — 커맨드 로그는 틱으로만 적히니 리플레이는 멈춘 것을 모른다.
   */
  private held = 0;
  /** 세우기 직전의 보간 비율. 세운 동안 이 모습 그대로 그린다(0으로 그리면 한 틱 뒤로 튄다). */
  private heldAlpha = 0;
  private readonly buf: Command[] = [];

  private readonly world: World;
  private readonly renderer: Renderer;
  private readonly input: InputState;
  private readonly hooks: LoopHooks;

  constructor(world: World, renderer: Renderer, input: InputState, hooks: LoopHooks = {}) {
    this.world = world;
    this.renderer = renderer;
    this.input = input;
    this.hooks = hooks;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    this.renderer.app.ticker.add(() => this.frame());
  }

  private frame(): void {
    const now = performance.now();
    let dt = now - this.last;
    this.last = now;
    if (dt > 250) dt = 250;
    this.accumulator += dt;

    if (this.paused || this.held !== 0) {
      this.accumulator = 0;
      this.renderer.render(this.held !== 0 ? this.heldAlpha : 0);
      this.hooks.onFrame?.(this.fps, this.simMs);
      return;
    }

    let steps = 0;
    const t0 = performance.now();
    // 틱 사이에도 멈춤을 본다. 판이 끝나면(`onTick`이 `pause`) 이 프레임의 나머지 따라잡기 틱을 돌리지 않는다 — 처음에는
    // 끝난 뒤에도 몇 틱을 더 돌려, 장수가 쓰러진 뒤 퇴각 시간이 지나 잃어야 할 병사가 그 틈에 빠져나가 살아 돌아왔다
    // (리뷰가 짚었다).
    while (this.accumulator >= TICK_MS && steps < MAX_CATCHUP && !this.paused) {
      this.accumulator -= TICK_MS;
      steps++;
      this.tick();
    }
    if (steps > 0) this.simMs = performance.now() - t0;
    if (steps === MAX_CATCHUP) this.accumulator = 0;

    this.renderer.render(this.accumulator / TICK_MS);

    this.frames++;
    this.fpsClock += dt;
    if (this.fpsClock >= 500) {
      this.fps = Math.round((this.frames * 1000) / this.fpsClock);
      this.frames = 0;
      this.fpsClock = 0;
    }
    this.hooks.onFrame?.(this.fps, this.simMs);
  }

  private tick(): void {
    const next = this.world.tick + 1;
    const input = this.input;

    // 이동 입력은 바뀔 때만 기록한다. 로그가 작아야 서버로 보내기 편하다.
    if (input.dx !== this.lastDx || input.dy !== this.lastDy) {
      this.lastDx = input.dx;
      this.lastDy = input.dy;
      this.log.push(cmd(next, CmdType.MoveDir, input.dx, input.dy));
    }
    // 선택이 먼저 들어가야 뒤따르는 명령이 올바른 분대에 붙는다.
    if (input.pendingSelect !== null) {
      const mask = normalizeSelect(input.pendingSelect, this.world.sqUsed);
      if (mask !== 0) this.log.push(cmd(next, CmdType.SelectSquad, mask));
      input.pendingSelect = null;
    }
    if (input.pendingOrder !== null) {
      this.log.push(cmd(next, CmdType.Order, input.pendingOrder));
      input.pendingOrder = null;
    }
    if (input.pendingRally) {
      this.log.push(cmd(next, CmdType.Rally, input.pendingRally.x, input.pendingRally.y));
      input.pendingRally = null;
    }
    if (input.pendingPath.length > 0) {
      for (const p of input.pendingPath) {
        this.log.push(cmd(next, p.start ? CmdType.Rally : CmdType.Waypoint, p.x, p.y));
      }
      input.pendingPath.length = 0;
    }
    if (input.clearRally) {
      this.log.push(cmd(next, CmdType.Recall));
      input.clearRally = false;
    }
    if (input.roar) {
      this.log.push(cmd(next, CmdType.Roar));
      input.roar = false;
    }
    if (input.strike) {
      this.log.push(cmd(next, CmdType.Strike));
      input.strike = false;
    }

    this.log.take(next, this.buf);
    this.world.step(this.buf);
    this.hooks.onTick?.(this.world);
  }

  /** 부대 고르기 동안 시뮬레이션을 세우거나 푼다. 전투가 끝난 뒤(`pause`)에는 풀어도 돌지 않는다. */
  hold(on: boolean, why: number = HOLD_PICK): void {
    const was = this.held;
    if (on && was === 0) this.heldAlpha = Math.min(1, this.accumulator / TICK_MS);
    this.held = on ? was | why : was & ~why;
    // 세운 까닭이 둘일 수 있다(부대 고르기, 나갈지 묻는 창). 하나가 풀려도 다른 하나가 남았으면 그대로 서 있는다 —
    // 한 칸짜리였다면 묻는 창을 닫는 순간 부대 고르기 중인 판이 돌았을 것이다.
    if (this.held !== 0 || was === 0) return;
    // 풀 때 쌓인 시간을 버린다. 세운 동안의 시간이 한꺼번에 틱으로 쏟아지면 안 된다. 보간은 세운 자리에서 잇는다 —
    // 0에서 다시 시작하면 풀리는 첫 프레임이 한 틱 뒤로 되감겼다(리뷰가 짚었다). 틱 수와는 무관하다.
    this.last = performance.now();
    this.accumulator = this.heldAlpha * TICK_MS;
  }

  /** 전투가 끝나면 시뮬레이션만 멈춘다. 렌더링과 입력은 계속 돈다. */
  pause(): void {
    this.paused = true;
  }

  get isPaused(): boolean {
    return this.paused;
  }

  /** 기록된 입력을 처음부터 다시 돌려 같은 결과가 나오는지 본다. */
  snapshotLog(): number[] {
    return this.log.toJSON();
  }
}
